import {html} from 'lit';
import '../icon/icon.js';
import '../form/label/label.js';
import Base from '../base.js';
import styles from './input-text.css' assert {type: 'css'};
/**
 * @element inv-input-text
 *
 * @class InputText
 * @extends Base
 * @classdesc InputText component
 *
 * @property {String} name - name of input
 * @property {Boolean} disabled - is the input disabled
 * @property {String} error - error message
 * @property {String} label - label of input
 * @property {String} helper - help message below input
 * @property {String} placeholder
 * @property {String} suffix - text suffix of the input
 * @property {String} icon - icon name in the spreadsheet
 * @property {String} icon-src - icon src
 * @property {String} value - value of input
 *
 * @cssproperty --inv-font-family
 * @description The font family for the application.
 * @type {string}
 * @default "'Inter'"
 *
 * @cssproperty --inv-input-text-font-size
 * @description The font size for input text.
 * @type {string}
 * @default "1em"
 *
 * @cssproperty --inv-input-text-border-color
 * @description The border color for input text.
 * @type {string}
 * @default "#aabaca"
 *
 * @cssproperty --inv-input-hover-border-color
 * @description The hover border color for input text.
 * @type {string}
 * @default "#0e4dab"
 *
 * @cssproperty --inv-input-focus-border-color
 * @description The focus border color for input text.
 * @type {string}
 * @default "#33c5d1"
 *
 * @cssproperty --inv-input-disabled-background
 * @description The background color for disabled input text.
 * @type {string}
 * @default "#f0f5f9"
 *
 * @cssproperty --inv-input-disabled-border
 * @description The border color for disabled input text.
 * @type {string}
 * @default "#dbe1e7"
 *
 * @cssproperty --inv-input-placeholder-color
 * @description The font color for the placeholder text in an input field.
 * @type {string}
 * @default "#6d809c"
 *
 * @cssproperty --inv-input-error-border-color
 * @description The error border color for input text.
 * @type {string}
 * @default "#e22e59"
 *
 * @cssproperty --inv-input-suffix-color
 * @description The font color for the suffix text in an input field.
 * @type {string}
 * @default "#aabaca"
 */
export default class InputText extends Base {
    static get properties() {
        return {
            name: {
                type: String,
            },
            disabled: {
                type: Boolean,
                reflect: true,
            },
            error: {
                type: String,
            },
            label: {
                type: String,
            },
            helper: {
                type: String,
            },
            placeholder: {
                type: String,
            },
            suffix: {
                type: String,
            },
            icon: {
                type: String,
            },
            iconSrc: {
                type: String,
                attribute: 'icon-src',
            },
            value: {
                type: String,
                reflect: true,
            },
        };
    }

    constructor() {
        super();
        /**
         *
         * @type {"hidden"| "text"| "search"| "tel"| "url"| "email"| "password"|
         *                 "datetime"| "date"| "month"| "week"| "time"| "datetime-local"|
         *                 "number"| "range"| "color"| "checkbox"| "radio"| "file"|
         *                 "submit"| "image"| "reset"| "button"}
         */
        this.type = 'text';
        this.disabled = false;
        this.name = 'input';
        this.placeholder = '';
        this.value = '';
    }

    getStyleSheets() {
        return [...super.getStyleSheets(), styles];
    }

    get #iconId() {
        return `input-icon` + this.getId();
    }

    get #icon() {
        return this._getElementById(this.#iconId);
    }

    firstUpdated(changedProperties) {
        super.firstUpdated(changedProperties);
        const icon = this.#icon;
        if (icon && this.iconSrc) {
            icon.setAttribute('src', this.iconSrc);
        }
    }

    renderSuffix() {
        return this.suffix
            ? html`
                  <div class="input-suffix-container">${this.suffix}</div>
              `
            : null;
    }

    renderIcon() {
        return this.icon
            ? html`
                    <inv-icon
                            @click="${this.buildHandler('icon-click')}"
                            id="${this.#iconId}"
                            width="${this.iconWidth ?? '1.2em'}"
                            height="${this.iconHeight ?? '1.2em'}"
                            color="var(--inv-input-icon-color, #AABACA)"
                            class="end-addornment "
                            name="${this.icon}"
                    /></inv-icon>`
            : null;
    }

    renderHelper() {
        return this.helper
            ? html`
                    <inv-form-label style=" --inv-form-label-font-size:0.6em;
                                            --inv-form-label-color:#8BA1B6;
                                            --inv-form-label-margin-top:0.4em;
                                          "
                                    type="helper" name="${this.name}" ">${this.helper}
                    </inv-form-label>`
            : null;
    }

    renderError() {
        return this.error
            ? html`
                    <inv-form-label style="
                                            --inv-form-label-color:#E22E59;
                                            --inv-form-label-margin-top:0.4em;
                                          "
                                    type="error" name="${this.name}"
                    ">${this.error}
                    </inv-form-label>`
            : null;
    }

    renderLabel() {
        return this.label
            ? html`
                  <inv-form-label name="${this.name}" style="--inv-form-label-margin-bottom:0.4em;">
                      ${this.label}
                  </inv-form-label>
              `
            : null;
    }

    handleEvent(e, eventName) {
        this.emit(eventName, {value: e.target.value, name: this.name});
    }

    onFocusIn(e) {
        this.handleEvent(e, 'focus-in');
    }

    onFocusOut(e) {
        this.handleEvent(e, 'focus-out');
    }

    onChange(e) {
        this.handleEvent(e, 'change');
    }

    renderInput(childNodes) {
        return html`
            <div class="input-container ${this.getCss(['error', 'disabled'])}">
                <input
                    @input="${this.buildHandler('change')}"
                    @focusin="${this.buildHandler('focus-in')}"
                    @focusout="${this.buildHandler('focus-out')}"
                    @blur="${this.buildHandler('blur', null, {bubbles: false})}"
                    .value="${this.value}"
                    class="${this.getCss('error')}"
                    type="${this.type ?? 'text'}"
                    id="${this.getId()}"
                    name="${this.name}"
                    placeholder="${this.placeholder}"
                    ?disabled="${this.disabled}"
                />
                ${childNodes}
            </div>
        `;
    }

    renderContent() {
        const nodes = [];
        if (this.suffix) {
            nodes.push(this.renderSuffix());
        }
        if (this.icon) {
            nodes.push(this.renderIcon());
        }
        return html`
            ${this.renderLabel()} ${this.renderInput(nodes)} ${this.renderHelper()} ${this.renderError()}
        `;
    }

    render() {
        return this.renderContent();
    }
}

InputText.register();
